import React from 'react';
import { useI18n } from '../../context/I18nContext';
import CableTessera from './CableTessera';
import WarningIcon from '../ui/WarningIcon';
import { Icons } from '../icons/Icons';
import { getCableDisplayName } from '../../utils/cableLogic';

const NestedCableList = ({
  nodes = [],
  settings,
  onCableClick,
  onEditCable,
  onEditMultipolarCable,
  onDeleteCable,
  onDeleteMultipolarCable,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  draggedIndex,
  dragOverIndex,
  editingIndex,
  editingMultipolarIndex,
  showDeleteButtons,
  onToggleDelete,
  isSelectionModeEnabled,
  selectedCables,
  onToggleSelection,
  availableSheaths,
  getCurrentGroupTitle,
  handleGroupTitleChange,
  handleGroupTitleBlur,
  handleGroupTitleKeyDown,
  validateCable
}) => {
  const { t } = useI18n();

  /* 
    Modificato `renderSingle` per accettare un override opzionale per nascondere la siglatura e il badge guaina.
  */
  const renderSingle = (node, hideSiglaturaOverride = false, hideSheathBadgeOverride = false) => {
    // Logica di visualizzazione siglatura:
    // 1. Se override è true (ereditato da genitore multiplo), NASCONDI.
    // 2. Altrimenti, usa logica locale: nascondi se è un nodo multiplo ridondante.
    const isRedundantMultiple = node.type === 'multiple' && !settings.marcaturePerCollegamento;
    const showSiglatura = !hideSiglaturaOverride && !isRedundantMultiple;

    return node.cables.map(cable => {
      const isDragging = draggedIndex === cable.originalIndex;
      const isDragOver = dragOverIndex === cable.originalIndex && draggedIndex !== cable.originalIndex;
      const validation = validateCable(cable, node.cables, settings.lingua);
      const hasIssues = !validation.isValid;
      const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);

      return (
        <div
          key={`nested-single-${cable.originalIndex}`}
          className="relative pr-2"
          draggable={settings.isDragModeEnabled}
          onDragStart={(e) => handleDragStart?.(e, cable.originalIndex)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver?.(e, cable.originalIndex)}
          onDragEnter={(e) => handleDragEnter?.(e, cable.originalIndex)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop?.(e, cable.originalIndex)}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            transform: isDragging ? 'scale(0.95)' : isDragOver ? 'translateY(-1px)' : 'none',
            boxShadow: isDragOver ? '0 4px 8px rgba(59, 130, 246, 0.3)' :
              hasIssues ? '0 2px 4px rgba(245, 158, 11, 0.2)' : 'none'
          }}
        >
          {settings.isDragModeEnabled && (
            <div
              className={`absolute left-4 text-gray-400 hover:text-blue-600 transition-colors z-10 ${cable.poli && Array.isArray(cable.poli)
                ? 'top-7'
                : 'top-1/2 transform -translate-y-1/2'
                }`}
            >
              <Icons.DragHandle className="w-4 h-4" />
            </div>
          )}

          {!settings.isDragModeEnabled && hasIssues && (
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
              <WarningIcon issues={validation.errors} className="flex-shrink-0" />
            </div>
          )}
          <CableTessera
            cable={cable}
            validateCable={() => validation}
            displaySiglatura={displaySiglatura}
            onCableClick={onCableClick}
            onEditCable={onEditCable}
            onEditMultipolarCable={onEditMultipolarCable}
            onDeleteCable={onDeleteCable}
            onDeleteMultipolarCable={onDeleteMultipolarCable}
            showSiglatura={showSiglatura}
            clickable={!settings.isDragModeEnabled && !isSelectionModeEnabled}
            settings={settings}
            isDragEnabled={settings.isDragModeEnabled}
            isDragging={isDragging}
            isDragOver={isDragOver}
            hasIssues={hasIssues}
            editingIndex={editingIndex}
            editingMultipolarIndex={editingMultipolarIndex}
            showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
            onToggleDelete={() => onToggleDelete(cable.originalIndex)}
            isSelectionModeEnabled={isSelectionModeEnabled}
            selectedCables={selectedCables}
            onToggleSelection={onToggleSelection}
            availableSheaths={availableSheaths}
            hideSheathBadge={hideSheathBadgeOverride}
          />
        </div>
      );
    });
  };

  // Helper per estrarre il nome del gruppo dalla chiave
  const extractGroupName = (node) => {
    // Estrai il nome del gruppo da groupCode, defaultTitle, o groupKey
    let groupName = node.groupCode || node.defaultTitle;

    // Se groupName non è disponibile, prova a estrarlo da groupKey o id
    if (!groupName) {
      const key = node.groupKey || node.id;
      // Se la chiave contiene "forced_group_", estrai il nome del gruppo
      if (key && key.startsWith('forced_group_')) {
        // Rimuovi "forced_group_" e il contatore finale (es: "_0")
        const match = key.match(/^forced_group_(.+?)(?:_\d+)?$/);
        if (match) {
          groupName = match[1];
        } else {
          groupName = key;
        }
      } else {
        groupName = key;
      }
    }

    return groupName;
  };

  const getNodeTitle = (node) => {
    if (node.type === 'group') {
      const groupName = extractGroupName(node);

      // Usa getCurrentGroupTitle per ottenere il titolo personalizzato
      const title = getCurrentGroupTitle?.(groupName);
      if (title) return title;

      // Fallback al nome del gruppo
      return groupName || node.label || t('cableList.group');
    }

    if (node.type === 'sheath' && node.sheathId) {
      const sheath = availableSheaths?.find(s => s.id === node.sheathId);
      if (sheath) {
        return sheath.fullLabel || `${t('cableList.sheathLabel')} ${sheath.label}`;
      }
      return `${t('cableList.sheathLabel')} ${node.sheathId}`;
    }

    const firstCable = node.cables?.[0];
    if (!firstCable) return node.label || t('cableList.cable');

    if (settings?.marcaturePerCollegamento) {
      const cableNumbers = node.cables
        ?.map(cable => `#${(cable.originalIndex ?? 0) + 1}`)
        .join(', ');
      return `${t('cableList.cableLabel')} ${cableNumbers}`;
    }

    const displaySiglatura = getCableDisplayName(firstCable, settings.lingua, settings);
    return `${t('cableList.cableLabel')} ${displaySiglatura}`;
  };

  /*
    renderNode aggiornato per supportare la propagazione della visibilità della siglatura e del badge guaina.
    inheritHideSiglatura: se true, i figli non mostreranno la siglatura.
    inheritHideSheathBadge: se true, i figli non mostreranno il badge guaina.
  */
  const renderNode = (node, nestingLevel = 0, inheritHideSiglatura = false, inheritHideSheathBadge = false) => {
    if (node.type === 'single') {
      return (
        <React.Fragment key={node.id}>
          {renderSingle(node, inheritHideSiglatura, inheritHideSheathBadge)}
        </React.Fragment>
      );
    }

    const isSheath = node.type === 'sheath';
    const isGroup = node.type === 'group';
    const isMultiple = node.type === 'multiple';
    const title = getNodeTitle(node);

    // Determina se i figli devono nascondere la siglatura.
    // Se questo nodo è Multiplo e le impostazioni lo richiedono, allora nascondi nei figli.
    // Oppure se è già nascosto dal padre.
    const shouldHideChildrenSiglatura = inheritHideSiglatura || (isMultiple && !settings.marcaturePerCollegamento);

    // Determina se i figli devono nascondere il badge guaina.
    // 1. Se il nodo è MULTIPLO, resetta il nascondere (il badge va mostrato).
    // 2. Altrimenti, se è guaina o eredita nascondere, nascondi.
    const shouldHideChildrenSheathBadge = isMultiple ? false : (inheritHideSheathBadge || isSheath);

    const badge = isSheath
      ? t('cableList.sheath')
      : isGroup
        ? (node.groupCode || node.defaultTitle || t('cableList.group'))
        : t('cableList.multiple');
    const totalCables = node.cables?.length || 0;
    const subGroupCount = node.subGroups?.length || 0;
    const hasIssues = node.cables?.some(cable => !validateCable(cable, node.cables, settings.lingua).isValid);
    const allSelected = node.cables?.every(cable => selectedCables.has(cable.originalIndex));
    const currentLevel = node.level ?? 1;

    const handleHeaderClick = () => {
      if (!settings.isDragModeEnabled && !isSelectionModeEnabled && node.cables?.[0]) {
        const first = node.cables[0];
        onCableClick?.(first.siglatura, first.originalIndex);
      }
    };

    const handleToggleNodeSelection = (e) => {
      e.stopPropagation();
      if (!isSelectionModeEnabled) return;
      node.cables.forEach(cable => {
        const isSelected = selectedCables.has(cable.originalIndex);
        if (allSelected && isSelected) {
          onToggleSelection(cable.originalIndex);
        } else if (!allSelected && !isSelected) {
          onToggleSelection(cable.originalIndex);
        }
      });
    };

    const childContent = node.subGroups?.length > 0
      ? node.subGroups.map(child => renderNode(child, nestingLevel + 1, shouldHideChildrenSiglatura, shouldHideChildrenSheathBadge))
      : renderSingle(node, shouldHideChildrenSiglatura, shouldHideChildrenSheathBadge);

    const infoText = subGroupCount > 0
      ? `${totalCables === 1 ? `1 ${t('cableList.cable')}` : `${totalCables} ${t('cableList.cables')}`} ${t('cableList.inSubgroups', { count: subGroupCount, word: subGroupCount === 1 ? t('cableList.subgroup') : t('cableList.subgroups') })}`
      : totalCables === 1 ? `1 ${t('cableList.cable')}` : `${totalCables} ${t('cableList.cables')}`;

    const indentWrapperClass = currentLevel > 1 ? '' : '';
    const containerPaddingClass = currentLevel > 1 ? 'py-3 pl-3 pr-0' : 'p-3 pr-0';

    return (
      <div
        key={node.id}
        className={`border rounded-lg ${containerPaddingClass} transition-colors cursor-pointer w-full ${hasIssues
          ? 'bg-amber-50 border-amber-200 hover:bg-amber-100 border-l-4 border-l-amber-400'
          : isSheath
            ? 'bg-sky-50 border-sky-200 hover:bg-sky-100 border-l-4 border-l-sky-400'
            : isGroup
              ? 'bg-purple-50 border-purple-200 hover:bg-purple-100 border-l-4 border-l-purple-400'
              : 'bg-green-50 border-green-200 hover:bg-green-100 border-l-4 border-l-green-400'
          }`}
        onClick={handleHeaderClick}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1 mr-4">
            <div className="flex items-center gap-3">
              {isSelectionModeEnabled && (
                <label className="flex items-center cursor-pointer flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleToggleNodeSelection}
                    className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                  />
                </label>
              )}

              {isGroup ? (
                <input
                  type="text"
                  value={getCurrentGroupTitle?.(extractGroupName(node)) ?? title}
                  onChange={(e) => handleGroupTitleChange?.(extractGroupName(node), e.target.value)}
                  onBlur={() => handleGroupTitleBlur?.(extractGroupName(node))}
                  onKeyDown={(e) => handleGroupTitleKeyDown?.(e, extractGroupName(node))}
                  className="w-full px-3 py-2 text-base font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  placeholder={t('cableList.group')}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="font-medium text-base">{title}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0 pr-2">
            <div className="text-sm text-gray-600 text-right">
              {infoText}
            </div>

            <div className={`px-4 py-1 rounded-full text-sm font-medium ml-2 ${isSheath
              ? 'bg-sky-100 text-sky-800'
              : isGroup
                ? 'bg-purple-100 text-purple-800'
                : 'bg-green-100 text-green-800'
              }`}>
              {badge}
            </div>

            {hasIssues && (
              <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                ⚠️ {t('cableList.hasProblems')}
              </span>
            )}
          </div>
        </div>

        <div className={`${indentWrapperClass}`}>
          {/* La linea verticale di gruppo è ora integrata nel bordo sinistro del box del gruppo */}
          <div
            className="space-y-2"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {childContent}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 min-w-0 w-full">
      {nodes.map(node => renderNode(node, 0))}
    </div>
  );
};

export default NestedCableList;